import React, { useState } from 'react';
import { 
  X, 
  Columns, 
  SlidersHorizontal, 
  Trophy, 
  Heart, 
  Check, 
  Sparkles, 
  ExternalLink, 
  Layers, 
  ArrowLeft, 
  ArrowRight,
  Maximize2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Button from './Button';

export default function ComparisonModal({
  isOpen,
  onClose,
  entryA,
  entryB,
  onVote,
  onNextMatchup,
  hasMoreMatchups = false
}) {
  const [viewMode, setViewMode] = useState('split'); // 'split' | 'slider' | 'scorecard'
  const [sliderPos, setSliderPos] = useState(50);
  const [votedSide, setVotedSide] = useState(null); // 'A' | 'B' | null

  // Detailed Rubric Scoring (for jury/in-depth comparison)
  const [scoresA, setScoresA] = useState({ hierarchy: 8, typography: 9, originality: 8, craft: 9 });
  const [scoresB, setScoresB] = useState({ hierarchy: 9, typography: 8, originality: 9, craft: 8 });

  if (!isOpen || !entryA || !entryB) return null;

  const totalScoreA = ((scoresA.hierarchy + scoresA.typography + scoresA.originality + scoresA.craft) / 4).toFixed(1);
  const totalScoreB = ((scoresB.hierarchy + scoresB.typography + scoresB.originality + scoresB.craft) / 4).toFixed(1);

  const handleVote = (side) => {
    setVotedSide(side);
    const targetEntry = side === 'A' ? entryA : entryB;
    if (onVote) onVote(targetEntry.id);
  };

  const handleSliderMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    setSliderPos((x / rect.width) * 100);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(5, 5, 10, 0.9)',
      backdropFilter: 'blur(16px)',
      zIndex: 1300,
      display: 'flex',
      flexDirection: 'column',
      animation: 'fadeIn 0.2s ease',
      padding: '16px'
    }} onClick={onClose}>
      
      {/* Top Header Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 24px',
        background: 'var(--bg-surface)',
        borderRadius: '20px',
        border: '1px solid var(--border-subtle)',
        marginBottom: '16px',
        boxShadow: 'var(--shadow-md)'
      }} onClick={e => e.stopPropagation()}>
        
        {/* Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ padding: '8px', borderRadius: '10px', background: 'var(--primary-subtle)', color: 'var(--primary)' }}>
            <Columns size={18} />
          </div>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 800 }}>Side-by-Side Design Comparison</h3>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              Directly evaluate visual hierarchy, composition, and typography craft
            </span>
          </div>
        </div>

        {/* View Mode Switcher */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            display: 'flex',
            background: 'var(--bg-base)',
            padding: '3px',
            borderRadius: '10px',
            border: '1px solid var(--border-subtle)'
          }}>
            {[
              { id: 'split', label: 'Side-by-Side Dual', icon: Columns },
              { id: 'slider', label: 'Curtain Diff Swipe', icon: SlidersHorizontal },
              { id: 'scorecard', label: 'Jury Scorecard', icon: Trophy }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setViewMode(tab.id)}
                className="tactile-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '7px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  background: viewMode === tab.id ? 'var(--primary)' : 'transparent',
                  color: viewMode === tab.id ? '#FFFFFF' : 'var(--text-secondary)',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <tab.icon size={13} />
                {tab.label}
              </button>
            ))}
          </div>

          {onNextMatchup && (
            <button
              onClick={onNextMatchup}
              className="tactile-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 14px',
                borderRadius: '10px',
                background: 'var(--bg-base)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Next Matchup <ChevronRight size={14} />
            </button>
          )}

          <button
            onClick={onClose}
            className="tactile-btn"
            style={{
              padding: '8px',
              borderRadius: '10px',
              background: 'transparent',
              border: 'none',
              color: 'var(--text-tertiary)',
              cursor: 'pointer'
            }}
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Main Canvas Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        gap: '20px',
        borderRadius: '24px'
      }} onClick={e => e.stopPropagation()}>

        {/* ── MODE 1: Side-by-Side Dual View ── */}
        {viewMode === 'split' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', width: '100%', height: '100%' }}>
            
            {/* Design A */}
            <div style={{
              background: 'var(--bg-surface)',
              borderRadius: '24px',
              border: `2px solid ${votedSide === 'A' ? 'var(--primary)' : 'var(--border-subtle)'}`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: votedSide === 'A' ? '0 0 32px rgba(123, 63, 255, 0.4)' : 'none',
              transition: 'all 0.25s ease'
            }}>
              {/* Card Header */}
              <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-base)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ background: 'var(--primary)', color: '#FFFFFF', padding: '3px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 800 }}>DESIGN A</span>
                  <div>
                    <h4 style={{ fontSize: '0.9375rem', fontWeight: 800 }}>{entryA.title}</h4>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>By {entryA.author_name}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {entryA.software_used?.map(s => (
                    <span key={s} className="tool-chip">{s}</span>
                  ))}
                </div>
              </div>

              {/* Preview Image */}
              <div style={{ flex: 1, background: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                <img src={entryA.preview_url} alt={entryA.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>

              {/* Action Bar */}
              <div style={{ padding: '16px 20px', background: 'var(--bg-base)', borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                  Current: <strong>{entryA.votes + (votedSide === 'A' ? 1 : 0)} votes</strong>
                </span>
                
                <button
                  onClick={() => handleVote('A')}
                  className="tactile-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    background: votedSide === 'A' ? 'var(--accent)' : 'var(--primary)',
                    color: votedSide === 'A' ? '#000000' : '#FFFFFF',
                    fontSize: '0.875rem',
                    fontWeight: 800,
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {votedSide === 'A' ? <Check size={16} /> : <Heart size={16} />}
                  {votedSide === 'A' ? 'Voted for Design A' : 'Vote for Design A'}
                </button>
              </div>
            </div>

            {/* Design B */}
            <div style={{
              background: 'var(--bg-surface)',
              borderRadius: '24px',
              border: `2px solid ${votedSide === 'B' ? 'var(--primary)' : 'var(--border-subtle)'}`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: votedSide === 'B' ? '0 0 32px rgba(123, 63, 255, 0.4)' : 'none',
              transition: 'all 0.25s ease'
            }}>
              {/* Card Header */}
              <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-base)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ background: '#3BF686', color: '#000000', padding: '3px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 800 }}>DESIGN B</span>
                  <div>
                    <h4 style={{ fontSize: '0.9375rem', fontWeight: 800 }}>{entryB.title}</h4>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>By {entryB.author_name}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {entryB.software_used?.map(s => (
                    <span key={s} className="tool-chip">{s}</span>
                  ))}
                </div>
              </div>

              {/* Preview Image */}
              <div style={{ flex: 1, background: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                <img src={entryB.preview_url} alt={entryB.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>

              {/* Action Bar */}
              <div style={{ padding: '16px 20px', background: 'var(--bg-base)', borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                  Current: <strong>{entryB.votes + (votedSide === 'B' ? 1 : 0)} votes</strong>
                </span>

                <button
                  onClick={() => handleVote('B')}
                  className="tactile-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    background: votedSide === 'B' ? 'var(--accent)' : 'var(--primary)',
                    color: votedSide === 'B' ? '#000000' : '#FFFFFF',
                    fontSize: '0.875rem',
                    fontWeight: 800,
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {votedSide === 'B' ? <Check size={16} /> : <Heart size={16} />}
                  {votedSide === 'B' ? 'Voted for Design B' : 'Vote for Design B'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── MODE 2: Interactive Curtain Diff Swipe Slider ── */}
        {viewMode === 'slider' && (
          <div style={{
            flex: 1,
            background: '#000000',
            borderRadius: '24px',
            border: '1px solid var(--border-subtle)',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'ew-resize',
            userSelect: 'none'
          }} onMouseMove={handleSliderMove}>
            
            {/* Background: Design B */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={entryB.preview_url} alt={entryB.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              <div style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(0,0,0,0.75)', padding: '6px 14px', borderRadius: '999px', color: '#3BF686', fontWeight: 800, fontSize: '0.8125rem' }}>
                DESIGN B: {entryB.title}
              </div>
            </div>

            {/* Foreground: Design A with dynamic width clip */}
            <div style={{
              position: 'absolute',
              inset: 0,
              width: `${sliderPos}%`,
              overflow: 'hidden',
              borderRight: '3px solid #FFFFFF',
              boxShadow: '0 0 20px rgba(0,0,0,0.8)'
            }}>
              <div style={{ width: '100vw', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={entryA.preview_url} alt={entryA.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                <div style={{ position: 'absolute', top: '20px', left: '20px', background: 'rgba(0,0,0,0.75)', padding: '6px 14px', borderRadius: '999px', color: '#C09AFF', fontWeight: 800, fontSize: '0.8125rem' }}>
                  DESIGN A: {entryA.title}
                </div>
              </div>
            </div>

            {/* Central Drag Handle */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: `${sliderPos}%`,
              transform: 'translate(-50%, -50%)',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: '#FFFFFF',
              color: '#000000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
              pointerEvents: 'none'
            }}>
              <SlidersHorizontal size={18} />
            </div>
          </div>
        )}

        {/* ── MODE 3: In-Depth Multi-Criteria Scorecard ── */}
        {viewMode === 'scorecard' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', width: '100%' }}>
            
            {/* Scorecard A */}
            <div style={{ background: 'var(--bg-surface)', borderRadius: '24px', padding: '28px', border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--primary-text)', textTransform: 'uppercase' }}>DESIGN A</span>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800 }}>{entryA.title}</h3>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--primary-text)' }}>{totalScoreA}</div>
                  <span style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)' }}>/ 10 RATING</span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '28px' }}>
                {[
                  { id: 'hierarchy', label: 'Visual Hierarchy & Layout' },
                  { id: 'typography', label: 'Typography & Readability' },
                  { id: 'originality', label: 'Concept & Freshness' },
                  { id: 'craft', label: 'Execution & Component Hygiene' }
                ].map(criterion => (
                  <div key={criterion.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{criterion.label}</span>
                      <strong style={{ color: 'var(--text-primary)' }}>{scoresA[criterion.id]}/10</strong>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={scoresA[criterion.id]}
                      onChange={e => setScoresA({ ...scoresA, [criterion.id]: parseInt(e.target.value) })}
                    />
                  </div>
                ))}
              </div>

              <Button variant="primary" style={{ marginTop: 'auto' }} onClick={() => handleVote('A')}>
                Submit Jury Score for Design A ({totalScoreA}★)
              </Button>
            </div>

            {/* Scorecard B */}
            <div style={{ background: 'var(--bg-surface)', borderRadius: '24px', padding: '28px', border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase' }}>DESIGN B</span>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800 }}>{entryB.title}</h3>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--accent)' }}>{totalScoreB}</div>
                  <span style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)' }}>/ 10 RATING</span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '28px' }}>
                {[
                  { id: 'hierarchy', label: 'Visual Hierarchy & Layout' },
                  { id: 'typography', label: 'Typography & Readability' },
                  { id: 'originality', label: 'Concept & Freshness' },
                  { id: 'craft', label: 'Execution & Component Hygiene' }
                ].map(criterion => (
                  <div key={criterion.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{criterion.label}</span>
                      <strong style={{ color: 'var(--text-primary)' }}>{scoresB[criterion.id]}/10</strong>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={scoresB[criterion.id]}
                      onChange={e => setScoresB({ ...scoresB, [criterion.id]: parseInt(e.target.value) })}
                    />
                  </div>
                ))}
              </div>

              <Button variant="primary" style={{ marginTop: 'auto' }} onClick={() => handleVote('B')}>
                Submit Jury Score for Design B ({totalScoreB}★)
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
